import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  StickyNote, Bell, Phone, Link2, Plus, Edit, Trash2, Save, X,
  CheckSquare, Square, ExternalLink, Clock, AlertTriangle, Check
} from 'lucide-react';
import api from '../lib/api';
import { parseApiDate } from '../lib/utils';
import toast from 'react-hot-toast';
import RichTextEditor from '../components/shared/RichTextEditor';
import RichTextDisplay from '../components/shared/RichTextDisplay';

const TABS = [
  { id: 'reminders', label: 'Reminders',   icon: Bell },
  { id: 'notes',     label: 'Notes',       icon: StickyNote },
  { id: 'calls',     label: 'Call Log',    icon: Phone },
  { id: 'links',     label: 'Quick Links', icon: Link2 },
];

// ── Ticket/Project link picker ────────────────────────────────
function LinkPicker({ ticketId, projectId, onChangeTicket, onChangeProject, tickets, projects }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label className="form-label">Link to Ticket (optional)</label>
        <select className="form-select" value={ticketId || ''} onChange={e => onChangeTicket(e.target.value || null)}>
          <option value="">No ticket</option>
          {tickets.map(t => <option key={t.id} value={t.id}>#{t.ticket_number} — {t.subject?.slice(0,50)}</option>)}
        </select>
      </div>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label className="form-label">Link to Project (optional)</label>
        <select className="form-select" value={projectId || ''} onChange={e => onChangeProject(e.target.value || null)}>
          <option value="">No project</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
    </div>
  );
}

// ── Linked badge ──────────────────────────────────────────────
function LinkedBadge({ ticketNumber, ticketSubject, projectName }) {
  const navigate = useNavigate();
  if (!ticketNumber && !projectName) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      {ticketNumber && (
        <button onClick={() => navigate(`/tickets/${ticketNumber}`)} style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 3,
          color: 'var(--accent)', fontSize: 11, fontWeight: 500,
        }}>
          <ExternalLink size={10} /> #{ticketNumber} {ticketSubject?.slice(0,30)}
        </button>
      )}
      {projectName && (
        <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          <ExternalLink size={10} /> {projectName}
        </span>
      )}
    </span>
  );
}

// ── REMINDERS TAB ─────────────────────────────────────────────
function RemindersTab({ tickets, projects }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState({ title: '', due_at: '', ticket_id: null, project_id: null });
  const [saving, setSaving]   = useState(false);
  const [showDone, setShowDone] = useState(false);

  const load = useCallback(() => {
    api.get('/workbook/reminders').then(d => setItems(d.reminders || [])).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function startNew() {
    setForm({ title: '', due_at: '', ticket_id: null, project_id: null });
    setEditing('new');
  }

  function startEdit(r) {
    const due = r.due_at ? parseApiDate(r.due_at).toISOString().slice(0,16) : '';
    setForm({ title: r.title, due_at: due, ticket_id: r.ticket_id, project_id: r.project_id });
    setEditing(r.id);
  }

  async function save() {
    if (!form.title.trim()) return toast.error('Title required');
    setSaving(true);
    try {
      const payload = { ...form, due_at: form.due_at || null };
      if (editing === 'new') await api.post('/workbook/reminders', payload);
      else await api.put(`/workbook/reminders/${editing}`, payload);
      toast.success('Saved');
      setEditing(null);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  async function toggle(r) {
    try {
      await api.put(`/workbook/reminders/${r.id}`, { ...r, completed: !r.completed });
      load();
    } catch (e) { toast.error(e.message); }
  }

  async function remove(id) {
    if (!window.confirm('Delete this reminder?')) return;
    try { await api.delete(`/workbook/reminders/${id}`); load(); } catch (e) { toast.error(e.message); }
  }

  const now      = new Date();
  const active   = items.filter(r => !r.completed);
  const done     = items.filter(r =>  r.completed);
  const overdue  = active.filter(r => r.due_at && parseApiDate(r.due_at) < now);
  const upcoming = active.filter(r => !r.due_at || parseApiDate(r.due_at) >= now);

  function ReminderRow({ r }) {
    const due      = r.due_at ? parseApiDate(r.due_at) : null;
    const isOverdue = due && due < now && !r.completed;
    const isDueSoon = due && !isOverdue && (due - now) < 24 * 60 * 60 * 1000;
    return (
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px',
        borderBottom: '1px solid var(--border)',
        background: isOverdue ? 'var(--red-light)' : undefined,
        opacity: r.completed ? 0.55 : 1,
      }}>
        <button onClick={() => toggle(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', color: r.completed ? 'var(--green)' : 'var(--text-muted)', flexShrink: 0 }}>
          {r.completed ? <CheckSquare size={18} /> : <Square size={18} />}
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 500, fontSize: 13, textDecoration: r.completed ? 'line-through' : undefined }}>{r.title}</div>
          {due && (
            <div style={{ fontSize: 11, marginTop: 2, color: isOverdue ? 'var(--red)' : isDueSoon ? 'var(--amber)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
              {isOverdue && <AlertTriangle size={10} />}
              <Clock size={10} />
              {isOverdue ? 'Overdue: ' : 'Due: '}{due.toLocaleString()}
            </div>
          )}
          <LinkedBadge ticketNumber={r.ticket_number} ticketSubject={r.ticket_subject} projectName={r.project_name} />
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <button className="btn btn-ghost btn-sm" style={{ padding: '3px 6px' }} onClick={() => startEdit(r)}><Edit size={12} /></button>
          <button className="btn btn-ghost btn-sm" style={{ padding: '3px 6px', color: 'var(--red)' }} onClick={() => remove(r.id)}><Trash2 size={12} /></button>
        </div>
      </div>
    );
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="spinner" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {overdue.length > 0 && <span style={{ color: 'var(--red)', fontWeight: 600 }}>{overdue.length} overdue · </span>}
          {active.length} active
        </div>
        <button className="btn btn-primary btn-sm" onClick={startNew}><Plus size={13} /> New Reminder</button>
      </div>

      {editing && (
        <div className="card card-sm mb-4">
          <div className="form-group">
            <label className="form-label">Title</label>
            <input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Reminder title..." autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Due Date & Time (optional)</label>
            <input className="form-input" type="datetime-local" value={form.due_at} onChange={e => setForm(f => ({ ...f, due_at: e.target.value }))} />
          </div>
          <LinkPicker ticketId={form.ticket_id} projectId={form.project_id} onChangeTicket={v => setForm(f => ({ ...f, ticket_id: v }))} onChangeProject={v => setForm(f => ({ ...f, project_id: v }))} tickets={tickets} projects={projects} />
          <div className="flex gap-2" style={{ marginTop: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}><Save size={13} /> {saving ? 'Saving…' : 'Save'}</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {overdue.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '.05em', padding: '0 0 6px' }}>Overdue</div>
          <div className="card" style={{ padding: 0, overflow: 'hidden', borderColor: '#fca5a5' }}>
            {overdue.map(r => <ReminderRow key={r.id} r={r} />)}
          </div>
        </div>
      )}

      {upcoming.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {overdue.length > 0 && <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', padding: '0 0 6px' }}>Upcoming</div>}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {upcoming.map(r => <ReminderRow key={r.id} r={r} />)}
          </div>
        </div>
      )}

      {active.length === 0 && !editing && (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)', fontSize: 13 }}>
          <Bell size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
          <p>No active reminders</p>
        </div>
      )}

      {done.length > 0 && (
        <div>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowDone(s => !s)} style={{ marginBottom: 8, fontSize: 12 }}>
            {showDone ? '▾' : '▸'} {done.length} completed
          </button>
          {showDone && (
            <div className="card" style={{ padding: 0, overflow: 'hidden', opacity: 0.7 }}>
              {done.map(r => <ReminderRow key={r.id} r={r} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── NOTES TAB ─────────────────────────────────────────────────
function NotesTab({ tickets, projects }) {
  const [notes, setNotes]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState({ title: '', body: '', ticket_id: null, project_id: null });
  const [saving, setSaving]   = useState(false);

  const load = useCallback(() => {
    api.get('/workbook/notes').then(d => setNotes(d.notes || [])).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function startNew() {
    setForm({ title: '', body: '', ticket_id: null, project_id: null });
    setEditing('new');
  }

  function startEdit(n) {
    setForm({ title: n.title, body: n.body, ticket_id: n.ticket_id, project_id: n.project_id });
    setEditing(n.id);
  }

  async function save() {
    if (!form.body.trim()) return toast.error('Note body required');
    setSaving(true);
    try {
      if (editing === 'new') await api.post('/workbook/notes', form);
      else await api.put(`/workbook/notes/${editing}`, form);
      toast.success('Saved');
      setEditing(null);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  async function remove(id) {
    if (!window.confirm('Delete this note?')) return;
    try { await api.delete(`/workbook/notes/${id}`); load(); } catch (e) { toast.error(e.message); }
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="spinner" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{notes.length} note{notes.length !== 1 ? 's' : ''}</div>
        <button className="btn btn-primary btn-sm" onClick={startNew}><Plus size={13} /> New Note</button>
      </div>

      {editing && (
        <div className="card card-sm mb-4">
          <div className="form-group">
            <label className="form-label">Title (optional)</label>
            <input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Note title..." autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Note</label>
            <RichTextEditor value={form.body} onChange={v => setForm(f => ({ ...f, body: v }))} placeholder="Write your note here..." minHeight={140} />
          </div>
          <LinkPicker ticketId={form.ticket_id} projectId={form.project_id} onChangeTicket={v => setForm(f => ({ ...f, ticket_id: v }))} onChangeProject={v => setForm(f => ({ ...f, project_id: v }))} tickets={tickets} projects={projects} />
          <div className="flex gap-2" style={{ marginTop: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}><Save size={13} /> {saving ? 'Saving…' : 'Save'}</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {notes.length === 0 && !editing ? (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)', fontSize: 13 }}>
          <StickyNote size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
          <p>No notes yet</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
          {notes.map(n => (
            <div key={n.id} className="card" style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="flex justify-between items-start">
                <div style={{ fontWeight: 600, fontSize: 15.5, lineHeight: 1.3 }}>{n.title || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Untitled</span>}</div>
                <div className="flex gap-1" style={{ flexShrink: 0, marginLeft: 8 }}>
                  <button className="btn btn-ghost btn-sm" style={{ padding: '2px 5px' }} onClick={() => startEdit(n)}><Edit size={13} /></button>
                  <button className="btn btn-ghost btn-sm" style={{ padding: '2px 5px', color: 'var(--red)' }} onClick={() => remove(n.id)}><Trash2 size={13} /></button>
                </div>
              </div>
              <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', flex: 1, maxHeight: 180, overflow: 'hidden', lineHeight: 1.6 }}><RichTextDisplay content={n.body} /></div>
              <div className="flex justify-between items-center">
                <LinkedBadge ticketNumber={n.ticket_number} ticketSubject={n.ticket_subject} projectName={n.project_name} />
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{parseApiDate(n.updated_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── CALLS TAB ─────────────────────────────────────────────────
function CallsTab({ tickets, projects }) {
  const [calls, setCalls]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState({ contact: '', duration_minutes: '', notes: '', ticket_id: null, project_id: null, called_at: '' });
  const [saving, setSaving]   = useState(false);

  const load = useCallback(() => {
    api.get('/workbook/calls').then(d => setCalls(d.calls || [])).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function startNew() {
    const now = new Date().toISOString().slice(0,16);
    setForm({ contact: '', duration_minutes: '', notes: '', ticket_id: null, project_id: null, called_at: now });
    setEditing('new');
  }

  function startEdit(c) {
    const calledAt = c.called_at ? parseApiDate(c.called_at).toISOString().slice(0,16) : '';
    setForm({ contact: c.contact, duration_minutes: c.duration_minutes, notes: c.notes || '', ticket_id: c.ticket_id, project_id: c.project_id, called_at: calledAt });
    setEditing(c.id);
  }

  async function save() {
    setSaving(true);
    try {
      if (editing === 'new') await api.post('/workbook/calls', form);
      else await api.put(`/workbook/calls/${editing}`, form);
      toast.success('Saved');
      setEditing(null);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  async function remove(id) {
    if (!window.confirm('Delete this call log entry?')) return;
    try { await api.delete(`/workbook/calls/${id}`); load(); } catch (e) { toast.error(e.message); }
  }

  function fmtDuration(mins) {
    if (!mins) return '—';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="spinner" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{calls.length} call{calls.length !== 1 ? 's' : ''} logged</div>
        <button className="btn btn-primary btn-sm" onClick={startNew}><Plus size={13} /> Log Call</button>
      </div>

      {editing && (
        <div className="card card-sm mb-4">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Contact / Caller</label>
              <input className="form-input" value={form.contact} onChange={e => setForm(f => ({ ...f, contact: e.target.value }))} placeholder="Name or number..." autoFocus />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Duration (minutes)</label>
              <input className="form-input" type="number" min="0" value={form.duration_minutes} onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))} placeholder="0" />
            </div>
          </div>
          <div className="form-group" style={{ marginTop: 12, marginBottom: 0 }}>
            <label className="form-label">Date & Time</label>
            <input className="form-input" type="datetime-local" value={form.called_at} onChange={e => setForm(f => ({ ...f, called_at: e.target.value }))} />
          </div>
          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Call notes..." />
          </div>
          <LinkPicker ticketId={form.ticket_id} projectId={form.project_id} onChangeTicket={v => setForm(f => ({ ...f, ticket_id: v }))} onChangeProject={v => setForm(f => ({ ...f, project_id: v }))} tickets={tickets} projects={projects} />
          <div className="flex gap-2" style={{ marginTop: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}><Save size={13} /> {saving ? 'Saving…' : 'Save'}</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {calls.length === 0 && !editing ? (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)', fontSize: 13 }}>
          <Phone size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
          <p>No calls logged yet</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {calls.map(c => (
            <div key={c.id} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <Phone size={14} style={{ color: 'var(--text-muted)', marginTop: 3, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 500, fontSize: 13 }}>{c.contact || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Unknown</span>}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtDuration(c.duration_minutes)}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{parseApiDate(c.called_at).toLocaleString()}</span>
                </div>
                {c.notes && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.5 }}>{c.notes}</div>}
                <LinkedBadge ticketNumber={c.ticket_number} ticketSubject={c.ticket_subject} projectName={c.project_name} />
              </div>
              <div className="flex gap-1" style={{ flexShrink: 0 }}>
                <button className="btn btn-ghost btn-sm" style={{ padding: '3px 6px' }} onClick={() => startEdit(c)}><Edit size={12} /></button>
                <button className="btn btn-ghost btn-sm" style={{ padding: '3px 6px', color: 'var(--red)' }} onClick={() => remove(c.id)}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── LINKS TAB ─────────────────────────────────────────────────
function LinksTab({ tickets, projects }) {
  const [links, setLinks]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState({ title: '', url: '', description: '', ticket_id: null, project_id: null });
  const [saving, setSaving]   = useState(false);

  const load = useCallback(() => {
    api.get('/workbook/links').then(d => setLinks(d.links || [])).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function startNew() {
    setForm({ title: '', url: '', description: '', ticket_id: null, project_id: null });
    setEditing('new');
  }

  function startEdit(l) {
    setForm({ title: l.title, url: l.url, description: l.description || '', ticket_id: l.ticket_id, project_id: l.project_id });
    setEditing(l.id);
  }

  async function save() {
    if (!form.title.trim() || !form.url.trim()) return toast.error('Title and URL required');
    setSaving(true);
    try {
      if (editing === 'new') await api.post('/workbook/links', form);
      else await api.put(`/workbook/links/${editing}`, form);
      toast.success('Saved');
      setEditing(null);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  async function remove(id) {
    if (!window.confirm('Delete this link?')) return;
    try { await api.delete(`/workbook/links/${id}`); load(); } catch (e) { toast.error(e.message); }
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="spinner" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{links.length} link{links.length !== 1 ? 's' : ''}</div>
        <button className="btn btn-primary btn-sm" onClick={startNew}><Plus size={13} /> Add Link</button>
      </div>

      {editing && (
        <div className="card card-sm mb-4">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Title</label>
              <input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Link title..." autoFocus />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">URL</label>
              <input className="form-input" type="url" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://..." />
            </div>
          </div>
          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label">Description (optional)</label>
            <input className="form-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Short description..." />
          </div>
          <LinkPicker ticketId={form.ticket_id} projectId={form.project_id} onChangeTicket={v => setForm(f => ({ ...f, ticket_id: v }))} onChangeProject={v => setForm(f => ({ ...f, project_id: v }))} tickets={tickets} projects={projects} />
          <div className="flex gap-2" style={{ marginTop: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}><Save size={13} /> {saving ? 'Saving…' : 'Save'}</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {links.length === 0 && !editing ? (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)', fontSize: 13 }}>
          <Link2 size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
          <p>No links saved yet</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {links.map(l => (
            <div key={l.id} className="card" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div className="flex justify-between items-start">
                <a href={l.url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, fontSize: 13, color: 'var(--accent)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <ExternalLink size={12} /> {l.title}
                </a>
                <div className="flex gap-1">
                  <button className="btn btn-ghost btn-sm" style={{ padding: '2px 5px' }} onClick={() => startEdit(l)}><Edit size={12} /></button>
                  <button className="btn btn-ghost btn-sm" style={{ padding: '2px 5px', color: 'var(--red)' }} onClick={() => remove(l.id)}><Trash2 size={12} /></button>
                </div>
              </div>
              {l.description && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{l.description}</div>}
              <div style={{ fontSize: 10, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.url}</div>
              <LinkedBadge ticketNumber={l.ticket_number} ticketSubject={l.ticket_subject} projectName={l.project_name} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────
export default function WorkbookPage() {
  const [tab, setTab]           = useState('reminders');
  const [tickets, setTickets]   = useState([]);
  const [projects, setProjects] = useState([]);
  const [dueCount, setDueCount] = useState(0);

  useEffect(() => {
    api.get('/tickets?limit=200').then(d => setTickets(d.tickets || [])).catch(() => {});
    api.get('/projects').then(d => setProjects(d.projects || [])).catch(() => {});
    api.get('/workbook/reminders/due-count').then(d => setDueCount(d.count || 0)).catch(() => {});
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div className="page-header">
        <h2>My Workbook</h2>
      </div>

      <div className="page-body">
        <select className="tab-mobile-select form-select" value={tab} onChange={e => setTab(e.target.value)}>
          {TABS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <div className="flex gap-1 mb-5 tab-bar" style={{ borderBottom: '1px solid var(--border)' }}>
          {TABS.map(t => {
            const Icon = t.icon;
            const isDue = t.id === 'reminders' && dueCount > 0;
            return (
              <button key={t.id} className="btn btn-ghost" onClick={() => setTab(t.id)} style={{
                borderRadius: '6px 6px 0 0', flexShrink: 0, position: 'relative',
                borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
                color: tab === t.id ? 'var(--accent)' : 'var(--text-secondary)',
                fontWeight: tab === t.id ? 600 : 400, paddingBottom: 10,
              }}>
                <Icon size={14} /> {t.label}
                {isDue && (
                  <span style={{
                    position: 'absolute', top: -2, right: -2,
                    background: 'var(--red)', color: '#fff',
                    borderRadius: '50%', width: 16, height: 16,
                    fontSize: 9, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{dueCount > 9 ? '9+' : dueCount}</span>
                )}
              </button>
            );
          })}
        </div>

        {tab === 'reminders' && <RemindersTab tickets={tickets} projects={projects} />}
        {tab === 'notes'     && <NotesTab     tickets={tickets} projects={projects} />}
        {tab === 'calls'     && <CallsTab     tickets={tickets} projects={projects} />}
        {tab === 'links'     && <LinksTab     tickets={tickets} projects={projects} />}
      </div>
    </div>
  );
}
